import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Container } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { getFaqPageContent } from '../../actions/cms'
import '../../styles/pages/FaqPage/FaqPageStyle.scss'
import Loader from '../../components/Loader/Loader'
import isEmpty from '../../utils/isEmpty'
import { usePrevious } from '../../utils'
import OmmCarousel from '../../components/Carousel/OmmCarousel'
import FaqItem from './FaqItem'
import FaqSearch from './FaqSearch'
import { getPath } from '../../MultilingualRouter/MultilingualRouter'
import Pages from '../../Enum/Pages'
import Markdown from '../../components/Markdown'
import HelmetTag from '../../utils/HelmetTag'

const FaqPage = () => {
  const dispatch = useDispatch()
  const lang = useSelector((state) => state.lang.lang)
  const prevLang = usePrevious(lang)
  const pageContent = useSelector((state) => state.cms.faq)
  const [mergedFaqList, setMergedFaqList] = useState([])
  const [filteredList, setFilteredList] = useState(pageContent?.topicList || [])
  const [topicList, setTopicList] = useState([])
  const [activeIndex, setActiveIndex] = useState(null)
  const [searchTerm, setSearchTerm] = useState(null)

  const formatSearchText = (text) => text?.toLowerCase()?.replace(/[^a-zA-Z0-9 ]/g, '') || ''

  useEffect(() => {
    if (!pageContent?.createdAt || lang !== prevLang) {
      dispatch(getFaqPageContent())
    }
    setTopicList([
      {
        id: 'all',
        title: pageContent?.allTopic,
        icon: 'done_all',
        totalItems: pageContent?.topicList?.reduce((count, topic) =>
          count + (topic.faqItems?.length || 0), 0) || 0
      },
      ...(pageContent?.topicList || [])
    ])
  }, [lang, dispatch, pageContent, prevLang])

  useEffect(() => {
    const faqList = topicList || []
    setMergedFaqList([])
    faqList.map((faq) => {
      setMergedFaqList((prev) => [...prev, ...(faq.faqItems || [])])
      return null
    })
  }, [topicList])

  useEffect(() => {
    setFilteredList(topicList)
  }, [topicList])

  const handleSearch = (searchText) => {
    const pureSearchText = formatSearchText(searchText)
    setSearchTerm(pureSearchText)
    setActiveIndex(null)
    setFilteredList(
      mergedFaqList.filter(
        (faq) =>
          formatSearchText(faq.title)?.includes(pureSearchText)
          || formatSearchText(faq.description)?.includes(pureSearchText)
      )
    )
  }
  const resetSearch = () => {
    setSearchTerm(null)
    setFilteredList([])
    setFilteredList(topicList)
  }
  const handleSuggestion = (suggestionId) => {
    setActiveIndex(suggestionId)
  }

  if (isEmpty(pageContent)) {
    return <Loader />
  }

  return (
    <main className="main bg-light py-1 faq-page">
      <HelmetTag pageTitle={pageContent.metaTitle} />
      <Container>
        <Row className="align-items-center">
          <Col xs={12} md={6} xl={5}>
            <h1 className="title">{pageContent?.title}</h1>
          </Col>
          <Col xs={12} md={6} xl={7}>
            {pageContent && (
              <FaqSearch
                searchProps={{
                  content: pageContent,
                  handleSearch,
                  resetSearch,
                  handleSuggestion,
                  mergedFaqList
                }}
              />
            )}
          </Col>
        </Row>
        {!searchTerm && (
          <section className="mt-4 topic-section" id="topic_all">
            <h2 className="section-title no-line text-muted h4">{pageContent?.topicsListTitle}</h2>
            {pageContent?.topicList?.length > 0 && (
              <OmmCarousel
                items={topicList}
                itemsPerSlide={{ xs: 2, sm: 2, md: 3, lg: 4, xl: 4 }}
                type="faqTopic"
                slideClass="py-3"
                itemClass="mb-3 mb-lg-4"
              />
            )}
          </section>
        )}

        {!searchTerm ? (
          topicList?.map((topic) => (
            <div key={topic.id}>
              {topic.faqItems?.length > 0 && (
                <section className="topic-section mb-3" id={`topic_${topic.id}`}>
                  <h2 className="section-title mb-3 h4">{topic?.title}</h2>
                  <Row className="accordion text-muted">
                    {topic.faqItems.map((faq, index) => (
                      <FaqItem
                        faqItemProps={{
                          faq,
                          index,
                          activeIndex,
                          setActiveIndex
                        }}
                        key={faq.id}
                      />
                    ))}
                  </Row>
                </section>
              )}
            </div>
          ))
        ) : (
          <div className="search-results">
            {filteredList?.length > 0 ? (
              <>
                <h2 className="section-title mb-3 h4">
                  {pageContent?.searchResultTitle}{' '}
                  <span className="h6 mb-0 ms-1 search-value">
                    &quot;<Markdown>{searchTerm}</Markdown>&quot;
                  </span>
                  <small className="ms-2 search-count align-self-end">
                    {`(${filteredList.length} ${pageContent.answers})`}
                  </small>
                </h2>
                <Row className="accordion mb-4 text-muted">
                  {filteredList?.map((faq, index) => (
                    <FaqItem
                      faqItemProps={{
                        faq,
                        index,
                        activeIndex,
                        setActiveIndex
                      }}
                      key={faq.id}
                    />
                  ))}
                </Row>
              </>
            ) : (
              <div className="text-center mb-5">
                <h2 className="h3">{pageContent?.emptyResultTitle}</h2>
                <h2 className="text-muted h4">&quot;{searchTerm}&quot;</h2>
                <Markdown>{pageContent?.emptyResultDescription}</Markdown>
                <Link className="btn btn-lg btn-primary mt-lg-3" to={getPath(Pages.contactUsPage)}>
                  {pageContent?.contactUsBtn}
                </Link>
              </div>
            )}
          </div>
        )}
      </Container>
    </main>
  )
}

export default FaqPage
